import { makeStyles, Spinner } from "@fluentui/react-components";
import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { LayoutContext } from "../../../../../context/LayoutContext";
import { $black, $white } from "../../../../../cssjs/colors";
import { FlightCardTypes } from "../../../../../enum/flights/FlightEnum";
import { useApiCall } from "../../../../../hooks/useApiCall";
import {
  AIRPORT_CODE_PARAMETER,
  EMPTY_STATE_MESSAGE,
} from "../../../../../utils/common/Constants";
import { NoDataCard } from "../../../../common/no-data-card/NoDataCard";
import { KPIHalfDonut } from "./KPIHalfDonut";
import { KPIHeader } from "./KPIHeader";

interface KPIDataItem {
  type: string;
  kpiValue?: number;
}
const baseURL = process.env.REACT_APP_API_BASE_URL;
const hopSettingsURL = process.env.REACT_APP_API_GET_HOP_SETTINGS;

interface KPICardProps {
  airportCode: string;
  cardName: FlightCardTypes;
  kpiConfigData: any;
  kpitype: string;
}

export const KPICard: React.FunctionComponent<KPICardProps> = ({
  airportCode,
  cardName,
  kpiConfigData,
  kpitype,
}): React.JSX.Element => {
  const classes = useStyles();
  const resource = {
    redirectToPage: "flights",
  };

  const { selectedFilter } = useContext(LayoutContext);

  const {
    data: data,
    error: kpiDataError,
    isLoading: kpiIsLoading,
  } = useApiCall<KPIDataItem[] | KPIDataItem | null>({
    url: kpiConfigData?.url,
    dependencies: [airportCode, kpiConfigData?.url],
  });

  let overallData = Array.isArray(data)
    ? data.find((item) => item.type === "Overall")
    : data && "type" in data && data.type === "Overall"
    ? data
    : null;

  const { data: hopSettingsData } = useApiCall<any>({
    url: `${baseURL}${hopSettingsURL}`
      .replace(AIRPORT_CODE_PARAMETER, airportCode)
      .replace("{kpiType}", kpitype),
    dependencies: [airportCode],
  });

  return (
    <div className={`${classes.cardContainer}`} data-testid="kpi-card">
      <Link
        to={`/${resource.redirectToPage}?kpiType=${kpitype}`}
        key={kpitype}
        className={`text-d-none`}
        style={{ color: $black }}
      >
        {kpiIsLoading ? (
          <div className={`${classes.loader}`}>
            <Spinner
              labelPosition="below"
              label="Loading..."
              className={classes.cardLoader}
            />
          </div>
        ) : kpiDataError ? (
          <NoDataCard message={EMPTY_STATE_MESSAGE.Data} />
        ) : (
          <div>
            <KPIHeader
              cardName={cardName}
              data={data}
              selectedFilter={selectedFilter}
              kpiConfigData={kpiConfigData}
              hopSettingsData={hopSettingsData}
              kpiType={kpitype}
            />
            {Array.isArray(data) && data.length > 1 ? (
              <div className={`${classes.donutContainer}`}>
                {data
                  .filter(
                    (item: any) =>
                      item.type === "Arrivals" || item.type === "Departures"
                  )
                  .map((item: any, idx: number) => (
                    <KPIHalfDonut
                      key={idx}
                      percentage={Math.round(item?.kpiValue ?? 0)}
                      width={400}
                      height={70}
                      outerRadius={110}
                      innerRadius={80}
                      data={item}
                    />
                  ))}
              </div>
            ) : (
              <div className={`${classes.donutContainer}`}>
                <KPIHalfDonut
                  percentage={Math.round(overallData?.kpiValue ?? 0)}
                  width={400}
                  height={70}
                  outerRadius={110}
                  innerRadius={80}
                  data={overallData}
                />
              </div>
            )}
          </div>
        )}
      </Link>
    </div>
  );
};

const useStyles = makeStyles({
  cardContainer: {
    padding: "8px",
    height: "180px",
    boxSizing: "border-box",
    border: `2px solid $white`,
    borderRadius: "8px",
    backgroundColor: $white,
    position: "relative",
    overflow: "hidden",
  },
  loader: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },
  cardLoader: {
    height: "100%",
    minHeight: "173px",
  },
  donutContainer: {
    position: "absolute",
    top: "60px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
});
